import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Row, Col, Input, Typography, Card, Spin, Empty,
  Divider, Button, message, Skeleton, Tag, Badge, Alert,
  Grid
} from 'antd';
import { motion } from 'framer-motion';
import {
  SearchOutlined,
  ShoppingCartOutlined,
  EyeOutlined,
  UpOutlined,
  ShopOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { FixedSizeList as List } from 'react-window';
import CategorySelector from '../../components/CategorySelector';
import HeroBanner from '../../components/HeroBanner';
import PromotionSlider from '../../components/PromotionSlider';
import { formatImageUrl } from '../../services/api';
import { capitalizeFirst } from '../../utils/stringUtils';
import './HomePage.css';
import 'react-lazy-load-image-component/src/effects/blur.css';

const { Title, Text } = Typography;
const { Search } = Input;
const { Meta } = Card;
const { useBreakpoint } = Grid;

// Recibe prop cart (array de productos en el carrito)
const HomePage = ({ products = [], categories = [], loading = false, addToCart, error, onRetry, cart = [] }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Ensure selectedCategory is initialized as string (or null) from URL
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [expandedCategories, setExpandedCategories] = useState(false);
  const [expandingCategories, setExpandingCategories] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isReducedMotion, setIsReducedMotion] = useState(false);
  const pageTopRef = useRef(null);
  const screens = useBreakpoint();

  // Escuchar scroll para mostrar botón "volver arriba"
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Agrupar y filtrar productos de manera eficiente
  const processedData = React.useMemo(() => {
    // 1. Filtrar productos por búsqueda si existe
    const filteredBySearch = searchQuery.trim()
      ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : products;

    // 2. Agrupar por categoría
    const grouped = filteredBySearch.reduce((acc, product) => {
      const categoryName = product.categoryName || 'Sin categoría';
      if (!acc[categoryName]) acc[categoryName] = [];
      acc[categoryName].push(product);
      return acc;
    }, {});

    // 3. Filtrar categorías según categoría seleccionada
    let categoryNames = Object.keys(grouped);
    if (selectedCategory) {
      const matchedCategory = categories.find(cat => String(cat.id) === selectedCategory);
      if (matchedCategory && grouped[matchedCategory.name]) {
        categoryNames = [matchedCategory.name];
      } else if (!matchedCategory) {
        // Fallback: si no hay match por ID, tal vez es por nombre
        const matchedByName = categories.find(cat => cat.name.toLowerCase() === selectedCategory.toLowerCase());
        if (matchedByName && grouped[matchedByName.name]) {
          categoryNames = [matchedByName.name];
        } else {
          categoryNames = [];
        }
      } else {
        categoryNames = [];
      }
    }

    // 4. Limitar categorías si no está expandido
    const displayedCategories = expandedCategories ? categoryNames : categoryNames.slice(0, 5);

    return {
      grouped,
      displayedCategories
    };
  }, [products, categories, searchQuery, selectedCategory, expandedCategories]);

  const { grouped: groupedProducts, displayedCategories } = processedData;

  // Obtener productos promocionados
  const promotedProducts = React.useMemo(() =>
    products.filter(product => product.promotions && product.promotions.length > 0),
    [products]
  );

  // Obtener ids de productos en el carrito
  const cartIds = cart.map(item => item.id);

  // Manejar la búsqueda
  const handleSearch = (value) => {
    setSearchQuery(value);
    setSearchParams({ ...(selectedCategory && { category: selectedCategory }), ...(value && { search: value }) });
  };

  // Seleccionar categoría (llamado por CategorySelector)
  const handleCategorySelect = (categoryIdFromSelector) => {
    // Mapear los IDs del CategorySelector a las categorías reales
    const categoryMapping = {
      'labiales': 'Labiales',
      'aretes': 'Aretes',
      'collares': 'Collares',
      'cremas': 'Cremas',
      'perfumes': 'Perfumes'
    };

    const categoryName = categoryMapping[categoryIdFromSelector];

    if (categoryName) {
      setSelectedCategory(categoryIdFromSelector);

      // Buscar la sección de la categoría y hacer scroll
      const categorySection = document.getElementById(`category-${categoryName.toLowerCase()}`);
      if (categorySection) {
        categorySection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  };

  // Limpiar filtros
  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory(null);
    setSearchParams({});
  };

  // Efecto para sincronizar estado con URL (cuando URL cambia por navegación o Header)
  useEffect(() => {
    const categoryIdFromUrl = searchParams.get('category'); // string or null
    const searchQueryFromUrl = searchParams.get('search') || '';

    if (selectedCategory !== categoryIdFromUrl) {
      setSelectedCategory(categoryIdFromUrl);
    }
    if (searchQuery !== searchQueryFromUrl) {
      setSearchQuery(searchQueryFromUrl);
    }
  }, [searchParams]); // Quitar selectedCategory y searchQuery de dependencias para evitar bucles si se actualizan desde otro lado y este useEffect los revierte.

  // Estado para controlar qué categorías muestran todos sus productos
  const [expandedProductCategories, setExpandedProductCategories] = useState({});
  const [loadingMoreInCategory, setLoadingMoreInCategory] = useState({});

  const toggleExpandCategoryProducts = (categoryName) => {
    const willExpand = !expandedProductCategories[categoryName];

    if (willExpand) {
      setLoadingMoreInCategory(prev => ({ ...prev, [categoryName]: true }));
      // Simular carga para mostrar skeletons y suavizar la transición
      setTimeout(() => {
        setExpandedProductCategories(prev => ({
          ...prev,
          [categoryName]: true
        }));
        setLoadingMoreInCategory(prev => ({ ...prev, [categoryName]: false }));
      }, 600);
    } else {
      setExpandedProductCategories(prev => ({
        ...prev,
        [categoryName]: false
      }));
    }
  };

  // Renderizar productos filtrados por categoría y búsqueda - Modificado para mostrar SIEMPRE en carrusel horizontal
  const renderProductsByCategory = (categoryName) => {
    const allProductsInCategory = groupedProducts[categoryName] || [];
    const isExpanded = expandedProductCategories[categoryName];
    const isLoadingMore = loadingMoreInCategory[categoryName];

    // Si no está expandido, solo mostrar los primeros 20
    const productsInCategory = isExpanded ? allProductsInCategory : allProductsInCategory.slice(0, 20);
    const hasMore = allProductsInCategory.length > 20;

    if (allProductsInCategory.length === 0) {
      return (
        <Empty
          description="No hay productos que coincidan con tu búsqueda"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }

    // Carrusel horizontal para todas las resoluciones
    return (
      <div className="product-carousel-container">
        <div className="products-carousel">
          {productsInCategory.map(product => (
            <div key={product.id} className="carousel-item">
              <MagazineProductCard
                product={product}
                onAddToCart={addToCart}
                onViewDetails={() => navigateToProduct(product.id)}
                isReducedMotion={isReducedMotion}
                isMobileView={isMobile}
                inCart={cartIds.includes(product.id)}
              />
            </div>
          ))}

          {isLoadingMore && (
            // Mostrar skeletons mientras "carga" el resto
            [1, 2, 3, 4].map(i => (
              <div key={`skeleton-${i}`} className="carousel-item">
                <div className="magazine-card skeleton-card-refined">
                  <div className="skeleton-image-area">
                    <Skeleton.Node active style={{ width: '100%', height: '240px' }} />
                  </div>
                  <div style={{ padding: '15px' }}>
                    <Skeleton active title={true} paragraph={{ rows: 1 }} />
                  </div>
                </div>
              </div>
            ))
          )}

          {hasMore && !isLoadingMore && (
            <div className="carousel-item view-more-item">
              <Card
                className="magazine-card view-more-card"
                onClick={() => toggleExpandCategoryProducts(categoryName)}
              >
                <div className="view-more-content">
                  <div className="view-more-icon">
                    {isExpanded ? <UpOutlined /> : <ShoppingCartOutlined />}
                  </div>
                  <Text className="view-more-text">
                    {isExpanded ? 'Ver menos' : `Ver ${allProductsInCategory.length - 20} más`}
                  </Text>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Botón "Mostrar más/menos" para categorías
  const renderShowMoreButton = () => {
    const totalCategories = Object.keys(groupedProducts).length;

    if (totalCategories <= 5) return null;

    return (
      <div className="show-more-container">
        <Button
          type="primary"
          ghost={false}
          loading={expandingCategories}
          onClick={() => {
            if (!expandedCategories) {
              setExpandingCategories(true);
              setTimeout(() => {
                setExpandedCategories(true);
                setExpandingCategories(false);
              }, 800);
            } else {
              setExpandedCategories(false);
            }
          }}
          className="show-more-button"
        >
          {expandedCategories ? "Mostrar menos categorías" : "Mostrar más categorías"}
        </Button>
      </div>
    );
  };

  // Get category-specific class
  const getCategoryClass = (name) => {
    name = name.toLowerCase();
    if (name.includes('perfume')) return 'category-perfume';
    if (name.includes('anillo')) return 'category-anillo';
    if (name.includes('arete')) return 'category-arete';
    if (name.includes('uñas') || name.includes('unas')) return 'category-unas';
    if (name.includes('labial')) return 'category-labial';
    if (name.includes('paleta')) return 'category-paleta';
    if (name.includes('rubor')) return 'category-rubor';
    return '';
  };

  // Animaciones con Framer Motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: screens.md ? 0.1 : 0.05 // Reducir tiempo de escalonamiento en móviles
      }
    }
  };

  const categoryVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: isReducedMotion ? 0.2 : 0.5, // Reducir duración si se prefiere movimiento reducido
        type: isReducedMotion ? 'tween' : 'spring' // Cambiar tipo de animación si se prefiere movimiento reducido
      }
    }
  };

  // Función para navegar a detalles del producto - Asegurar scroll al inicio
  const navigateToProduct = (productId) => {
    navigate(`/product/${productId}`);
    window.scrollTo(0, 0);
  };

  // Determinar si estamos en modo móvil
  const isMobile = !screens.md;

  // Detectar preferencias de reducción de movimiento
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setIsReducedMotion(mediaQuery.matches);

    const handleChange = (e) => setIsReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Scroll al inicio cuando cambian las búsquedas o categorías
  useEffect(() => {
    // Solo hacer scroll si selectedCategory o searchQuery realmente cambian y no son la carga inicial
    // Esto evita el scroll en la carga inicial si hay parámetros en la URL
    const initialCategory = searchParams.get('category');
    const initialSearch = searchParams.get('search');

    if (selectedCategory !== initialCategory || searchQuery !== initialSearch) {
      if (pageTopRef.current) {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
    }
  }, [selectedCategory, searchQuery, searchParams]); // Añadir searchParams para cubrir todos los casos de cambio

  // Función para scroll cuando se hace clic en Explorar
  const handleExploreClick = () => {
    // Scroll hasta la sección de categorías
    const categoriesSection = document.querySelector('.categories-section');
    if (categoriesSection) {
      categoriesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Función para renderizar Skeletons durante la carga inicial
  const renderSkeletons = () => {
    return (
      <div className="categories-section">
        {[1, 2, 3].map(i => (
          <div key={`cat-skeleton-${i}`} className="category-container">
            <div className="category-title-container">
              <Skeleton.Button active size="large" style={{ width: 200, borderRadius: '10px' }} />
            </div>
            <div className="product-carousel-container">
              <div className="products-carousel">
                {[1, 2, 3, 4, 5].map(j => (
                  <div key={`prod-skeleton-${j}`} className="carousel-item">
                    <div className="magazine-card skeleton-card-refined">
                      <div className="skeleton-image-area">
                        <Skeleton.Node active style={{ width: '100%', height: '240px' }} />
                      </div>
                      <div style={{ padding: '15px' }}>
                        <Skeleton active title={true} paragraph={{ rows: 1 }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Divider className="category-divider" />
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Banner portada */}
      <div style={{
        position: 'relative',
        top: 0,
        left: 0,
        width: '100vw',
        height: isMobile ? 'auto' : '100vh',
        zIndex: 0,
        overflow: 'hidden',
        margin: 0,
        padding: 0,
      }}>
        <HeroBanner onExploreClick={handleExploreClick}>
          <CategorySelector
            categories={categories}
            selected={selectedCategory}
            onSelect={handleCategorySelect}
            isMobile={isMobile}
          />
        </HeroBanner>
      </div>

      {/* Slider de promociones */}
      {promotedProducts.length > 0 && (
        <PromotionSlider products={promotedProducts} addToCart={addToCart} />
      )}

      {/* Contenido principal */}
      <div style={{
        position: 'relative',
        width: '100%',
        zIndex: 1,
        background: 'transparent',
        padding: 0
      }} ref={pageTopRef}>

        {loading ? renderSkeletons() : (
          <>
            <div className="categories-section">
              {/* Título elegante sin contenedor de card */}
              <Title level={isMobile ? 3 : 2} className="section-title">
                Categorías
              </Title>

              {displayedCategories.length === 0 ? (
                <Empty description="No se encontraron productos" />
              ) : (
                <>
                  <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {displayedCategories.map(categoryName => {
                      return (
                        <motion.div
                          key={categoryName}
                          id={`category-${categoryName.toLowerCase()}`}
                          variants={categoryVariants}
                          className={`category-container ${getCategoryClass(categoryName)}`}
                        >
                          <div className="category-title-container">
                            <Title level={isMobile ? 5 : 4} className="category-title">
                              {capitalizeFirst(categoryName)}
                            </Title>
                          </div>

                          {renderProductsByCategory(categoryName)}
                          <Divider className="category-divider" />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                  {expandingCategories && (
                    <div className="expanding-skeletons">
                      {[1, 2].map(i => (
                        <div key={`exp-skeleton-${i}`} className="category-container">
                          <div className="category-title-container">
                            <Skeleton.Button active size="small" style={{ width: 150, borderRadius: '8px' }} />
                          </div>
                          <div className="product-carousel-container">
                            <div className="products-carousel">
                              {[1, 2, 3].map(j => (
                                <div key={`exp-prod-skeleton-${j}`} className="carousel-item">
                                  <div className="magazine-card skeleton-card-refined">
                                    <div className="skeleton-image-area">
                                      <Skeleton.Node active style={{ width: '100%', height: '240px' }} />
                                    </div>
                                    <div style={{ padding: '15px' }}>
                                      <Skeleton active title={true} paragraph={{ rows: 1 }} />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {renderShowMoreButton()}
            </div>
          </>
        )}

        {/* Botón para volver arriba */}
        {showScrollTop && (
          <motion.div
            className="scroll-to-top"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
          >
            <Button
              type="primary"
              shape="circle"
              icon={<UpOutlined />}
              size="large"
              style={{ background: 'linear-gradient(135deg, #e91e63, #9c27b0)', border: 'none' }}
            />
          </motion.div>
        )}
      </div>
    </>
  );
};

// Componente de tarjeta de producto con diseño de revista colorido - mantenemos el mismo
const MagazineProductCard = React.memo(({ product, onAddToCart, onViewDetails, isReducedMotion, isMobileView, inCart }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  // Use the prop if provided, otherwise determine from window width
  const isMobile = isMobileView !== undefined ? isMobileView :
    (typeof window !== 'undefined' && window.innerWidth <= 768);

  const handleCardClick = (e) => {
    if (isTouchDevice) {
      // If already expanded and clicking view/add button, let the button event handle it
      if (e.target.closest('.magazine-btn')) {
        return;
      }

      // Toggle expanded state on touch devices
      e.stopPropagation();
      setIsExpanded(prev => !prev);
    } else {
      // On desktop, click always navigates to detail
      onViewDetails();
    }
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    onAddToCart(product, 1);
    message.success(`${product.name} añadido al carrito`);
  };

  // Calculate discounted price if there's a promotion
  const getDiscountedPrice = () => {
    if (!product.promotions || product.promotions.length === 0) return null;

    const promotion = product.promotions[0];
    if (promotion.type === 'descuento') {
      return product.price * (1 - promotion.value / 100);
    }
    return null;
  };

  const discountedPrice = getDiscountedPrice();
  const hasPromotion = product.promotions && product.promotions.length > 0;
  const isOutOfStock = product.stock <= 0;
  const isLowStock = product.stock > 0 && product.stock <= 5;

  // Get appropriate stock status
  const getStockStatus = () => {
    if (isOutOfStock) return 'out-of-stock';
    if (isLowStock) return 'low-stock';
    return 'in-stock';
  };

  // Get stock message
  const getStockMessage = () => {
    if (isOutOfStock) return 'Agotado';
    if (isLowStock) return `¡Solo ${product.stock}!`;
    return 'Disponible';
  };

  const animationProps = isReducedMotion ? {
    initial: {},
    animate: {},
    whileHover: {},
    whileTap: {}
  } : {
    initial: { opacity: 0, y: 20 },
    whileInView: {
      opacity: 1, y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    },
    viewport: { once: true, margin: "50px" },
    whileHover: isMobile ? {} : { y: -5, transition: { duration: 0.2 } },
    whileTap: { scale: 0.98 }
  };

  return (
    <motion.div
      className={`magazine-card ${isExpanded ? 'expanded' : ''}`}
      {...animationProps}
      onClick={handleCardClick}
    >
      {/* Badge flotante de agregado al carrito */}
      {inCart && (
        <div className="product-in-cart-badge">
          <span className="product-in-cart-check">✔</span> En tu carrito
        </div>
      )}
      <div className="magazine-card-inner">
        {/* Card Front - Product Image and Details */}
        <div className="magazine-card-front">
          {/* Background with category-based colors */}
          <div
            className="magazine-image-wrapper"
            data-category={product.categoryName.toLowerCase()}
          >
            {/* Product Image */}
            <LazyLoadImage
              alt={product.name}
              src={formatImageUrl(product.image)}
              effect="blur"
              className="magazine-product-image"
              placeholderSrc="/assets/placeholder.png"
            />

            {/* Shimmer Effect */}
            <motion.div
              className="shimmer"
              animate={{}}
            />

            {/* Decorator Elements */}
            <div className="decoration-circle decoration-circle-1"></div>
            <div className="decoration-circle decoration-circle-2"></div>

            {/* Brand Decoration Element */}
            {hasPromotion && (
              <motion.div
                className="brand-element"
                animate={{ rotate: [0, 5, 0, -5, 0] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
            )}

            {/* Discount Badge with 3D Effect */}
            {hasPromotion && (
              <motion.div
                className="magazine-price-tag"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <motion.div
                  className="discount-percent"
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [5, 8, 5]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  {product.promotions[0].value}% OFF
                </motion.div>
              </motion.div>
            )}

            {/* Out of Stock Overlay */}
            {isOutOfStock && (
              <div className="out-of-stock-overlay">
                <motion.div
                  className="out-of-stock-message"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{
                    scale: 1,
                    opacity: 1,
                    rotate: [-5, -7, -5]
                  }}
                  transition={{
                    delay: 0.2,
                    duration: 2,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                >
                  Agotado
                </motion.div>
              </div>
            )}

            {/* Product Details Panel (slides up on hover or tap) */}
            <div className="magazine-details">
              {isTouchDevice && <div className="pull-indicator"></div>}

              {/* Category Badge */}
              <div
                className={`magazine-category-badge${inCart ? ' in-cart' : ''}`}
                data-category={product.categoryName.toLowerCase()}
              >
                {inCart && <span className="in-cart-check">✔ </span>}
                {product.categoryName}
              </div>

              {/* Product Title */}
              <h3 className="magazine-title">{capitalizeFirst(product.name)}</h3>

              {/* Product Colors */}
              {product.colores && product.colores.length > 0 && (
                <div className="magazine-colors-preview">
                  {product.colores.slice(0, 4).map((color, idx) => (
                    <span
                      key={idx}
                      className="magazine-color-dot"
                      style={{ backgroundColor: color.toLowerCase() }}
                      title={color}
                    />
                  ))}
                  {product.colores.length > 4 && (
                    <span className="magazine-color-more">+{product.colores.length - 4}</span>
                  )}
                </div>
              )}

              {/* Product Branches Preview (Elegant Tags Style) */}
              {product.sucursalesDisponibles && product.sucursalesDisponibles.length > 0 && (
                <div className="magazine-branches-elegant">
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%', marginBottom: 4 }}>
                    <EnvironmentOutlined className="elegant-branch-icon" style={{ marginRight: 4 }} />
                    <span className="elegant-branch-label">Disponible en:</span>
                  </div>
                  <div className="elegant-branch-tags">
                    {product.sucursalesDisponibles.map((s, idx) => (
                      <span key={idx} className="elegant-branch-tag" title={`${s.stock} unidades en ${s.sucursal_nombre}`}>
                        {s.sucursal_nombre}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stock Status */}
              <div className={`magazine-stock-status ${getStockStatus()}`}>
                <span className="stock-indicator"></span>
                <span>{getStockMessage()}</span>
              </div>

              {/* Price Display */}
              <div className="magazine-pricing">
                {discountedPrice ? (
                  <>
                    <span className="magazine-original-price">
                      Bs. {product.price.toFixed(2)}
                    </span>
                    <span className="magazine-current-price">
                      Bs. {discountedPrice.toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="magazine-current-price">
                    Bs. {product.price.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="magazine-actions">
                <button
                  className="magazine-btn view-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails();
                  }}
                  aria-label={`Ver detalles de ${product.name}`}
                >
                  <EyeOutlined />
                  Ver
                </button>

                <button
                  className="magazine-btn cart-btn"
                  disabled={isOutOfStock}
                  onClick={handleAddToCart}
                  aria-label={`Añadir ${product.name} al carrito`}
                >
                  <ShoppingCartOutlined />
                  Añadir
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

export default HomePage;
